/** One warm-up second, then median one-second slices across all concurrent connections. */
export interface TrafficResult { bytes: number; ms: number; mbps: number; capped: boolean; preliminary: boolean }
export class TrafficFailure extends Error { constructor(message: string, readonly bytes: number) { super(message); } }
export type Transfer = (signal: AbortSignal, arrived: (bytes: number) => void) => Promise<void>;
export const median = (values: number[]) => {
  const v = values.filter(Number.isFinite).sort((a, b) => a - b);
  return v.length ? (v[Math.floor(v.length / 2)]! + v[Math.floor((v.length - 1) / 2)]!) / 2 : 0;
};

export class TrafficMeter {
  bytes = 0;
  first: number | null = null;
  slices: number[] = [];
  add(bytes: number, at: number) {
    this.bytes += bytes;
    this.first ??= at;
    const after = at - this.first - 1000;
    if (after <= 0) return;
    const slot = Math.max(0, Math.ceil(after / 1000) - 1);
    this.slices[slot] = (this.slices[slot] ?? 0) + bytes;
  }
  rates(at: number) {
    const elapsed = this.first === null ? 0 : Math.max(0, at - this.first - 1000);
    const complete = Math.floor(elapsed / 1000);
    const full = Array.from({ length: complete }, (_, i) => (this.slices[i] ?? 0) * 8 / 1_000_000);
    const partial = elapsed % 1000;
    return { full, elapsed, short: partial > 0 ? (this.slices[complete] ?? 0) * 8 / partial / 1000 : 0 };
  }
}

/** Transfers continuously re-open fresh requests. The byte cap counts warm-up too. */
export async function steadyTraffic(transfer: Transfer, o: { streams: number; durationMs: number; byteLimit: number; now?: () => number; signal?: AbortSignal }): Promise<TrafficResult> {
  const now = o.now ?? (() => performance.now());
  const started = now(); const meter = new TrafficMeter(); const ctrl = new AbortController();
  const stop = () => ctrl.abort(); o.signal?.addEventListener('abort', stop, { once: true });
  if (o.signal?.aborted) ctrl.abort();
  const timer = setTimeout(stop, o.durationMs);
  let lastError: unknown;
  const arrived = (bytes: number) => {
    meter.add(bytes, now());
    const { full } = meter.rates(now());
    const recent = full.slice(-2); const middle = median(recent);
    const stableSlow = recent.length === 2 && middle > 0 && middle < 10 && Math.abs(recent[0]! - recent[1]!) / middle < 0.1;
    if (meter.bytes >= o.byteLimit || now() - started >= o.durationMs || stableSlow) stop();
  };
  try {
    await Promise.all(Array.from({ length: o.streams }, async () => {
      while (!ctrl.signal.aborted) {
        try { await transfer(ctrl.signal, arrived); } catch (e) { if (!ctrl.signal.aborted) lastError = e; break; }
      }
    }));
    if (o.signal?.aborted) throw new TrafficFailure('stopped', meter.bytes);
    const { full, short } = meter.rates(now());
    const rate = full.length ? median(full) : short;
    if (!Number.isFinite(rate) || rate <= 0) throw new TrafficFailure(lastError instanceof Error ? lastError.message : 'timeout', meter.bytes);
    return { bytes: meter.bytes, ms: Math.max(1, now() - started), mbps: Math.round(rate * 10) / 10, capped: ctrl.signal.aborted, preliminary: full.length < 2 };
  } finally { clearTimeout(timer); o.signal?.removeEventListener('abort', stop); }
}

export type StreamFetch = (url: string, init?: Omit<RequestInit, 'cache'> & { cache?: 'no-store' }) => Promise<Response>;
export function downloadTransfer(fetchImpl: StreamFetch, url: string, nonce = Math.random().toString(36).slice(2)): Transfer {
  let sequence = 0;
  return async (signal, arrived) => {
    const response = await fetchImpl(`${url}?n=${nonce}-${sequence++}`, { signal, cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('http');
    if ((response.headers.get('content-type') ?? '').includes('text/html') || !response.body) throw new Error('missing');
    const reader = response.body.getReader(); const stop = () => void reader.cancel().catch(() => undefined);
    signal.addEventListener('abort', stop, { once: true });
    try { while (!signal.aborted) { const r = await reader.read(); if (r.done) break; arrived(r.value.byteLength); } }
    finally { signal.removeEventListener('abort', stop); await reader.cancel().catch(() => undefined); }
  };
}
